import React from "react";

export const articleContent: Record<string, React.ReactNode> = {
  "how-to-sign-up-as-turo-host": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Becoming a Turo Host</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Getting Started Series
      </p>
      
      <p>
        Welcome to your first step toward earning money with your car! Signing up as a Turo host is <strong>free and takes about 15-20 minutes</strong>. This guide walks you through every single step so you know exactly what to expect.
      </p>
      
      <p>
        Don't worry if you feel nervous—everyone does at the start. By the end of this article, you'll have a verified Turo host account ready to list your first car.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Before You Start: Requirements Checklist</h2>
      
      <p>
        Before you begin, make sure you meet these basic requirements. If you don't check all the boxes yet, don't worry—we'll explain each one.
      </p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Age:</strong> You must be at least 21 years old (in the US, Canada, and Australia). In the UK and France, you can start at 18.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Valid Driver's License:</strong> You need an active driver's license that isn't expired, suspended, or restricted.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Email Address:</strong> You'll need an email you check regularly—Turo sends important booking notifications here.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Phone Number:</strong> A mobile phone for verification texts and guest communications.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Bank Account:</strong> A checking account where Turo can deposit your earnings (you'll set this up through Stripe).</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Social Security Number (US):</strong> Required for tax reporting. Turo will send you a 1099 form if you earn over $600.</div>
        </div>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Vehicle Requirements (For When You List)</h3>
      
      <p>You don't need to have a car ready to create your host account, but when you do list, your vehicle must:</p>
      
      <ul>
        <li>Be <strong>12 years old or newer</strong> (for 2025, this means 2013 model year or newer)</li>
        <li>Have <strong>fewer than 130,000 miles</strong> on the odometer</li>
        <li>Have a <strong>clean title</strong> (no salvage, rebuilt, or branded titles)</li>
        <li>Have <strong>no open safety recalls</strong> (Turo checks this automatically)</li>
        <li>Be <strong>registered and insured</strong> in your name (or have authorization from the owner)</li>
      </ul>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 1: Download the Turo App</h2>
      
      <p>
        While you can use Turo's website, we <strong>strongly recommend downloading the mobile app</strong>. As a host, you'll use the app daily to manage bookings, communicate with guests, and take check-in photos.
      </p>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📱 For iPhone (iOS)</p>
          <p className="mb-0">Open the App Store, search "Turo", and tap "Get" to download. The app icon is a white "T" on a purple background.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📱 For Android</p>
          <p className="mb-0">Open the Google Play Store, search "Turo", and tap "Install". Make sure you're downloading the official app by Turo Inc.</p>
        </div>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Pro Tip</p>
        <p className="mb-0">Enable notifications for the Turo app! Fast response times (within minutes) dramatically increase your booking acceptance rate and guest satisfaction.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 2: Create Your Account</h2>
      
      <p>
        Open the Turo app and tap <strong>"Sign up"</strong>. You have three options:
      </p>
      
      <ul>
        <li><strong>Email:</strong> Enter your email address and create a password (recommended for business accounts)</li>
        <li><strong>Google:</strong> Sign up using your Google account</li>
        <li><strong>Apple:</strong> Sign up using your Apple ID (iPhone only)</li>
      </ul>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Important</p>
        <p className="mb-0">Use an email you check regularly. Turo sends time-sensitive booking requests and important notifications here. If you miss a booking request, it auto-declines after 24 hours!</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Phone Verification</h3>
      
      <p>
        After entering your email, Turo will ask for your phone number. You'll receive a 6-digit verification code via text message. Enter this code to verify your phone.
      </p>
      
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Didn't receive the code?</p>
        <p className="mb-0">Wait 60 seconds, then tap "Resend code." If it still doesn't arrive, check that you entered your number correctly, including the country code. Some VoIP numbers (Google Voice, etc.) may not work.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 3: Complete Your Profile</h2>
      
      <p>
        Now Turo will ask you to set up your profile. This is what guests see when they consider booking your car, so take it seriously!
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Add Your Profile Photo</h3>
      
      <p>
        This is <strong>very important</strong>. Guests are more likely to book from hosts with friendly, clear profile photos. Think of it like meeting someone for the first time—you want to seem trustworthy and approachable.
      </p>
      
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ Good Profile Photo Tips</p>
        <ul className="mb-0 mt-2">
          <li>Use a clear, recent photo of your face</li>
          <li>Smile—it makes you look friendly and trustworthy</li>
          <li>Good lighting (natural light works best)</li>
          <li>Plain or simple background</li>
          <li>No sunglasses or hats covering your face</li>
        </ul>
      </div>
      
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Avoid These</p>
        <ul className="mb-0 mt-2">
          <li>Group photos (guests won't know who you are)</li>
          <li>Photos of your car, pet, or logo</li>
          <li>Blurry or dark photos</li>
          <li>Photos with filters that distort your appearance</li>
        </ul>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Fill Out Your Bio</h3>
      
      <p>
        Write a short bio (2-3 sentences) introducing yourself. Mention why you started hosting and what guests can expect from you. Keep it friendly and professional.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold mb-2">Example Bio:</p>
        <p className="italic text-muted-foreground mb-0">"Hi! I'm Sarah, a local small business owner. I started hosting on Turo in 2024 to help visitors explore our beautiful city. I keep my cars spotless and always respond quickly. Let me know if you have any questions!"</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 4: Identity Verification</h2>
      
      <p>
        This is where many new hosts get stuck, so pay close attention. Turo needs to verify that you are who you say you are before you can start hosting.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">What You'll Need to Submit</h3>
      
      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📋 Driver's License Photos</p>
          <p className="mb-2">You'll need to photograph both the <strong>front and back</strong> of your physical driver's license.</p>
          <ul className="mb-0 text-sm text-muted-foreground">
            <li>Must be the actual plastic card (not a screenshot or digital license)</li>
            <li>All four corners must be visible</li>
            <li>Text must be clearly readable</li>
            <li>No blur, glare, or shadows</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🤳 Selfie with License</p>
          <p className="mb-2">You may be asked to take a selfie holding your license next to your face.</p>
          <ul className="mb-0 text-sm text-muted-foreground">
            <li>Show your full head and shoulders</li>
            <li>Hold the license at face level, next to your cheek</li>
            <li>Remove hats and sunglasses</li>
            <li>Make sure both your face AND the license are in focus</li>
          </ul>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Common Rejection Reasons</p>
        <ul className="mb-0 mt-2">
          <li><strong>Blurry photos:</strong> Hold your phone steady and make sure the camera focuses</li>
          <li><strong>Glare on license:</strong> Move away from bright lights or windows</li>
          <li><strong>Cropped corners:</strong> The entire card must be visible</li>
          <li><strong>Screenshots:</strong> You must take actual photos, not screenshots of digital licenses</li>
          <li><strong>Expired license:</strong> Your license must be currently valid</li>
        </ul>
      </div>

      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ If Verification Fails</p>
        <p className="mb-0">Don't panic! Contact Turo support through the app (Menu → Help → Chat with us). Explain your situation and they'll guide you through resubmitting. Most verification issues are resolved within 24-48 hours.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 5: Set Up Your Payment Account (Stripe)</h2>
      
      <p>
        To receive your earnings, you need to connect a bank account through <strong>Stripe</strong>, Turo's payment processor. This happens when you list your first vehicle, but it's helpful to know what to expect.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">What You'll Need</h3>
      
      <ul>
        <li><strong>Bank account number</strong> and <strong>routing number</strong> (found on your checks or in your bank's mobile app)</li>
        <li><strong>Social Security Number</strong> (for US tax reporting)</li>
        <li><strong>Date of birth</strong></li>
        <li><strong>Home address</strong></li>
      </ul>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Why Stripe Asks for SSN</p>
        <p className="mb-0">This is required by US law for tax reporting. If you earn over $600 in a calendar year, Turo must report your earnings to the IRS. Your SSN is securely stored by Stripe—Turo never sees it.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">When Do You Get Paid?</h3>
      
      <p>
        Turo releases your earnings <strong>3 days after each trip ends</strong>. The money then takes 1-3 business days to reach your bank account, depending on your bank. So from trip end to cash in hand is typically <strong>4-6 business days</strong>.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 6: Explore the Host Dashboard</h2>
      
      <p>
        Congratulations! You now have a verified Turo host account. Before you list your first car, take a few minutes to explore the app.
      </p>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📊 Dashboard</p>
          <p className="mb-0">Shows your upcoming trips, earnings, and quick stats at a glance.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📅 Calendar</p>
          <p className="mb-0">View and manage your vehicle's availability. You can block dates when you need your car.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">💬 Messages</p>
          <p className="mb-0">Where you'll communicate with guests. Fast responses lead to better reviews!</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">⚙️ Settings</p>
          <p className="mb-0">Manage your profile, notifications, payment info, and trip preferences.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">What's Next?</h2>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">You're officially a Turo host! 🎉</p>
        <p className="text-center mb-0">
          Your account is set up and verified. The next step is to list your first vehicle. Check out our <strong>"How to List Your First Car"</strong> guide to create an optimized listing that attracts bookings.
        </p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Quick Reference: Account Setup Checklist</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Downloaded the Turo app and enabled notifications</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Created account with email/Google/Apple</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Verified phone number</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Added friendly profile photo</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Written bio introducing yourself</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Submitted driver's license photos</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Passed identity verification</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Ready to list your first vehicle!</div>
        </div>
      </div>
    </div>
  ),

  "how-much-can-you-make": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">The Real Numbers Behind Turo Hosting</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Financial Analysis Series
      </p>
      
      <p>
        This is the question every new host asks: <strong>"How much money can I actually make?"</strong>
      </p>
      
      <p>
        The honest answer? It depends on a lot of factors—and <strong>over 60% of new Turo hosts actually lose money</strong> because they don't understand the full picture before they start.
      </p>
      
      <p>
        This guide will walk you through every cost you need to consider, give you realistic numbers based on an economy car (which we recommend for beginners), and help you decide if Turo makes financial sense for you.
      </p>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Important Mindset Shift</p>
        <p className="mb-0">Don't think about gross earnings (what Turo pays you). Think about <strong>net profit</strong> (what you keep after ALL expenses). That's the only number that matters.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Full Cost Breakdown</h2>
      
      <p>
        Let's use a realistic example: a <strong>2017 Toyota Corolla</strong> purchased for Turo hosting. This is a popular beginner choice because it's reliable, affordable, and in-demand.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">1. Vehicle Purchase Cost</h3>
      
      <p>
        This is your biggest upfront investment. For a 2017 Toyota Corolla with around 80,000 miles in good condition:
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">💰 Purchase Price Range</p>
        <ul className="mb-0">
          <li><strong>Private sale:</strong> $8,000 - $11,000</li>
          <li><strong>Dealer purchase:</strong> $10,000 - $13,000</li>
          <li><strong>Our example:</strong> $9,500 (private sale, good condition)</li>
        </ul>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Economy vs. Luxury: The Math</p>
        <p className="mb-0">A $9,500 economy car vs. a $45,000 luxury SUV? The economy car often wins on ROI (return on investment). Luxury cars have higher daily rates but also higher insurance, maintenance, depreciation, and repair costs. Start with economy until you understand the business.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">2. Registration, Taxes & Title</h3>
      
      <p>
        These costs vary significantly by state, but you'll typically pay:
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <ul className="mb-0">
          <li><strong>Sales tax:</strong> 5-10% of purchase price (some states exempt private sales)</li>
          <li><strong>Registration fees:</strong> $50 - $500/year (varies wildly by state)</li>
          <li><strong>Title transfer:</strong> $15 - $100</li>
          <li><strong>Our example (California):</strong> ~$1,100 first year (high-tax state)</li>
        </ul>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">3. Initial Maintenance (Getting "Turo Ready")</h3>
      
      <p>
        Before your first rental, the car needs to be in <strong>excellent condition</strong>. Guests expect a clean, well-maintained vehicle, and problems lead to bad reviews that tank your business.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🔧 Initial Prep Costs (Example)</p>
        <ul className="mb-0">
          <li><strong>Pre-purchase inspection:</strong> $100 - $150</li>
          <li><strong>Oil change & fluids:</strong> $50 - $100</li>
          <li><strong>New tires (if needed):</strong> $400 - $600</li>
          <li><strong>Brake pads (if needed):</strong> $150 - $300</li>
          <li><strong>Windshield repair/replacement:</strong> $0 - $300</li>
          <li><strong>Detail cleaning:</strong> $100 - $200</li>
          <li><strong>Minor cosmetic fixes:</strong> $0 - $200</li>
          <li><strong>State inspection (if required):</strong> $20 - $50</li>
          <li className="font-semibold mt-2">Estimated Total: $300 - $1,500</li>
        </ul>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Pro Tip</p>
        <p className="mb-0">Always get a pre-purchase inspection from an independent mechanic (not the seller's mechanic). This $100-$150 can save you thousands by revealing hidden problems before you buy.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">4. Insurance</h3>
      
      <p>
        This is where many new hosts get confused. There are <strong>two types of insurance</strong> you need to understand:
      </p>
      
      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🛡️ During Trips (Turo's Coverage)</p>
          <p className="mb-2">When your car is rented out, Turo provides liability coverage through your protection plan. You don't need separate commercial insurance for trip time.</p>
          <p className="text-sm text-muted-foreground mb-0">Cost: Already included in Turo's fee structure</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🏠 Between Trips (Your Responsibility)</p>
          <p className="mb-2">When the car is parked and not rented, you need your own insurance. Options include:</p>
          <ul className="mb-0">
            <li><strong>Personal auto insurance:</strong> $100-$200/month (but may exclude commercial use—check your policy!)</li>
            <li><strong>Commercial auto insurance:</strong> $150-$400/month (required by some states for Turo)</li>
            <li><strong>Turo's off-trip insurance (where available):</strong> ~$50-$80/month</li>
          </ul>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Critical Warning</p>
        <p className="mb-0">Many personal auto insurance policies <strong>exclude coverage for commercial use</strong>. If you use your personal insurance and don't disclose Turo hosting, a claim could be denied AND your policy cancelled. Always check with your insurance company first!</p>
      </div>

      <p><strong>Our example:</strong> $120/month for personal insurance (policy allows rideshare/carshare use)</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">5. Depreciation (The Hidden Cost)</h3>
      
      <p>
        This is the cost most new hosts forget. Every mile your car drives, it loses value. This is real money, even if you don't "see" it until you sell.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">📉 Depreciation Math</p>
        <p className="mb-2">For <strong>older economy cars</strong>, depreciation is much lower than newer or luxury vehicles. A 7-8 year old Corolla with 80,000+ miles has already lost most of its value—it's near the "depreciation floor."</p>
        <p className="mb-2">Realistically, expect to lose about <strong>$800-$1,200 per year</strong> in value on an economy car at this stage.</p>
        <p className="mb-0">That works out to roughly <strong>$70-$100/month</strong>—a fraction of what you'd lose on a newer vehicle.</p>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Why Economy Cars Win</p>
        <p className="mb-0">A brand new BMW loses $5,000-$8,000 in the first year alone. A 7-year-old Corolla has already "bottomed out" on depreciation. This is the single biggest reason we recommend starting with older economy cars—your monthly depreciation cost is dramatically lower.</p>
      </div>

      <p><strong>Our example:</strong> ~$80/month depreciation (already near the floor for this vehicle class)</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">6. Ongoing Maintenance</h3>
      
      <p>
        Rental cars get driven hard. Expect more frequent maintenance than a personal vehicle.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🔧 Monthly Maintenance Budget</p>
        <ul className="mb-0">
          <li><strong>Oil changes:</strong> Every 5,000 miles (~$40-60 each)</li>
          <li><strong>Tire rotation:</strong> Every 5,000-7,500 miles (~$25-50)</li>
          <li><strong>Brake service:</strong> Every 30,000-50,000 miles (~$200-400)</li>
          <li><strong>Unexpected repairs:</strong> Budget for the unexpected</li>
          <li className="font-semibold mt-2">Rule of thumb: $0.08-$0.12 per mile</li>
        </ul>
      </div>

      <p><strong>Our example:</strong> ~$120/month (at 1,200 miles/month, $0.10/mile)</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">7. Cleaning</h3>
      
      <p>
        Every guest expects a spotless car. You'll need to clean between every single trip.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🧹 Cleaning Costs</p>
        <ul className="mb-0">
          <li><strong>DIY cleaning:</strong> ~$5 per trip (supplies + time)</li>
          <li><strong>Monthly car wash membership:</strong> ~$20/month</li>
        </ul>
      </div>

      <p>If you have 8-12 trips per month:</p>
      <p><strong>Our example:</strong> ~$70/month (DIY cleaning at $5/trip × 10 trips + $20 car wash membership)</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">8. Damage Claims & Deductibles</h3>
      
      <p>
        Damage happens. Not every trip, but it <em>will</em> happen eventually. Your protection plan determines your out-of-pocket cost.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🚗 Current Deductibles (2026)</p>
        <ul className="mb-0">
          <li><strong>Max protect plan (70%):</strong> $250 deductible</li>
          <li><strong>Balanced plan (80%):</strong> $1,500 deductible</li>
          <li><strong>Max earn plan (90%):</strong> $2,750 deductible</li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2 mb-0">All plans include up to $750,000 in third-party liability insurance.</p>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ The Reality of Damage</p>
        <p className="mb-2">On the Balanced or Max earn plans, small damage is <strong>your problem</strong>. A $1,500 dent on the Max earn plan? You pay all of it because it's below your $2,750 deductible.</p>
        <p className="mb-0">Budget for at least <strong>one significant damage incident per year</strong>. For economy cars, this might be $500-$1,500. For luxury cars, $2,000-$5,000+.</p>
      </div>

      <p><strong>Our example:</strong> ~$80/month reserve ($1,000/year damage budget ÷ 12)</p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Now Let's Talk About Earnings</h2>
      
      <p>
        Turo reports that cars earn an <strong>average of $906/month gross</strong>. But averages are deceiving—your actual earnings depend on:
      </p>
      
      <ul>
        <li><strong>Your market:</strong> Airport-adjacent locations earn more</li>
        <li><strong>Your vehicle:</strong> SUVs and unique cars often outperform sedans</li>
        <li><strong>Seasonality:</strong> Summer and holidays are peak; January-February is dead</li>
        <li><strong>Your reviews:</strong> 5-star hosts get more visibility and bookings</li>
        <li><strong>Your pricing:</strong> Dynamic pricing and discounts affect bookings</li>
      </ul>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Realistic Economy Car Earnings</h3>
      
      <p>
        For a 2017 Toyota Corolla in an average market with moderate demand:
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">📊 Monthly Earnings Estimate</p>
        <ul className="mb-0">
          <li><strong>Daily rate:</strong> $35-$50/day</li>
          <li><strong>Utilization:</strong> 50-65% (15-20 rental days/month)</li>
          <li><strong>Gross monthly:</strong> $525-$1,000</li>
          <li><strong>After Turo fees (Max protect 70% plan):</strong> $368-$700</li>
        </ul>
      </div>

      <p><strong>Our example:</strong> $600/month gross → $420/month after Turo's 30% cut (Max protect plan)</p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Profit Calculation</h2>
      
      <p>
        Now let's put it all together for our 2017 Toyota Corolla example:
      </p>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold mb-4">Monthly Profit Calculation</p>
        
        <div className="space-y-2 mb-4">
          <div className="flex justify-between">
            <span>Gross Earnings (before Turo fees)</span>
            <span className="font-semibold">$600</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>− Turo fees (30% on Max protect plan)</span>
            <span>-$180</span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span>Net from Turo</span>
            <span className="font-semibold">$420</span>
          </div>
        </div>
        
        <div className="space-y-2 mb-4">
          <p className="font-semibold">Monthly Expenses:</p>
          <div className="flex justify-between text-muted-foreground">
            <span>− Insurance</span>
            <span>-$120</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>− Depreciation</span>
            <span>-$80</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>− Maintenance</span>
            <span>-$120</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>− Cleaning</span>
            <span>-$70</span>
          </div>
          <div className="flex justify-between text-muted-foreground">
            <span>− Damage reserve</span>
            <span>-$80</span>
          </div>
        </div>
        
        <div className="flex justify-between border-t-2 pt-3 text-xl">
          <span className="font-bold">Monthly Net Profit</span>
          <span className="font-bold text-amber-600">-$50</span>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Wait... That's a Loss?</p>
        <p className="mb-0">Yes. In this realistic scenario with moderate utilization and the Max protect plan, <strong>the host is losing money</strong>. This is exactly why many Turo hosts struggle. They see "$600/month earnings" and forget about all the costs. The good news? With higher utilization and an economy car, you're not far from profitability.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">How to Actually Make Money</h2>
      
      <p>
        The math above isn't meant to scare you away—it's meant to show you what needs to change to be profitable:
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">1. Increase Utilization</h3>
      
      <p>
        The biggest lever is getting more bookings. At 50% utilization, you're losing money. At 70%+ utilization, the math starts working.
      </p>
      
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ At 70% Utilization (21 days/month)</p>
        <p className="mb-0">Gross: $840 → Net from Turo (70%): $588 → After expenses: <strong>+$118/month profit</strong>. Now you're making real money!</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">2. Lower Your Purchase Price</h3>
      
      <p>
        Buy smarter. A $7,000 car with lower depreciation changes the equation. Find deals through private sales, auctions, or motivated sellers.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">3. Choose a Better Market</h3>
      
      <p>
        Near airports, downtown areas, or tourist destinations = higher demand = higher utilization = profit.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">4. Optimize Your Pricing</h3>
      
      <p>
        Use Turo's dynamic pricing, offer weekly discounts, and adjust for seasonality. The goal is maximum utilization, not maximum daily rate.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">5. DIY Everything Possible</h3>
      
      <p>
        Do your own cleaning, basic maintenance, and minor repairs. Every dollar saved is a dollar of profit.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Risk of Losing Money</h2>
      
      <p>
        Let's be completely honest about the risks:
      </p>
      
      <div className="space-y-4 my-6">
        <div className="bg-card border border-red-500/30 rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">❌ Total Loss Scenario</p>
          <p className="mb-0">If your car is totaled, Turo reimburses you the "actual cash value" (what the car is worth now, not what you paid). On a financed car, you could still owe money after the payout.</p>
        </div>
        
        <div className="bg-card border border-red-500/30 rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">❌ Low Season Drought</p>
          <p className="mb-0">January and February can see utilization drop to 30% or less. You still owe insurance and registration during dead months.</p>
        </div>
        
        <div className="bg-card border border-red-500/30 rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">❌ Accumulated Damage</p>
          <p className="mb-0">Small dings, stains, and wear add up. After a year of rentals, your car may be worth significantly less than a similar low-mileage car.</p>
        </div>
        
        <div className="bg-card border border-red-500/30 rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">❌ Market Saturation</p>
          <p className="mb-0">If too many hosts join your area, prices drop and utilization suffers. This is happening in many markets.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Final Thoughts</h2>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">The Bottom Line</p>
        <p className="text-center mb-4">
          Turo can be profitable, but it's <strong>not passive income</strong> and it's <strong>not guaranteed profit</strong>. You're running a real business with real risks.
        </p>
        <p className="text-center mb-0">
          Start with your personal car to test demand in your market. Only buy a dedicated Turo vehicle after you've proven you can achieve 60%+ utilization consistently.
        </p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Your Homework Before Buying</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Research what similar cars rent for in your exact area</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Use Turo's Carculator to estimate earnings for your market</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Calculate ALL costs (use our breakdown above)</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Make sure profit is positive even at 50% utilization</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Have 3-6 months of expenses in reserve for slow seasons</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div>Only invest money you can afford to lose</div>
        </div>
      </div>
    </div>
  ),

  "renting-out-your-personal-car": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Starting with Your Personal Vehicle</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Getting Started Series
      </p>
      
      <p>
        Here's the smartest advice we can give a new Turo host: <strong>Don't buy a car for Turo until you've tested the waters with your personal vehicle first.</strong>
      </p>
      
      <p>
        Renting out your daily driver is the lowest-risk way to learn the Turo business—no upfront investment, no financing risk, and you can quit anytime if it's not for you.
      </p>
      
      <p>
        But it does require some planning. This guide will help you navigate the challenges of sharing a car you actually need for your own life.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 1: Assess Your Dependence on Your Vehicle</h2>
      
      <p>
        Before listing your car, honestly answer these questions:
      </p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">🤔</span>
          <div><strong>How do you commute to work?</strong> If you drive daily, you'll need alternative transportation during rentals.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">🤔</span>
          <div><strong>Do you have backup transportation?</strong> A partner's car, public transit, biking, or rideshare options?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">🤔</span>
          <div><strong>Can your schedule accommodate rentals?</strong> Weekend-only hosting is valid if that's what works.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">🤔</span>
          <div><strong>What's your car's current value?</strong> Renting a $30,000 car you're still financing is riskier than a $12,000 paid-off car.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">🤔</span>
          <div><strong>Are you emotionally attached?</strong> Guests will add wear, dings, and miles. Can you handle that?</div>
        </div>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The "Alternative Transportation" Test</p>
        <p className="mb-0">Before listing, try going one full week without your car. Use transit, bike, walk, rideshare, or borrow a car. If you can manage, you're ready to host. If it was miserable, Turo hosting with your personal car may not work for your lifestyle.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 2: Understand the Insurance Situation</h2>
      
      <p>
        This is <strong>critically important</strong> and where many new hosts make expensive mistakes.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">The Problem with Personal Insurance</h3>
      
      <p>
        Most personal auto insurance policies have a <strong>commercial use exclusion</strong>. This means:
      </p>
      
      <ul>
        <li>Your insurance <strong>does NOT cover your car while it's being rented on Turo</strong></li>
        <li>If you file a claim and they discover you were renting the car, they can <strong>deny the claim</strong></li>
        <li>They could potentially <strong>cancel your entire policy</strong> for misrepresentation</li>
      </ul>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Don't Hide Turo from Your Insurance</p>
        <p className="mb-0">Some hosts think "they'll never find out." Wrong. Insurance companies investigate claims. If they discover your car was listed on Turo when an accident happened, you could be denied coverage AND face policy cancellation. The risk isn't worth it.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">The Solution</h3>
      
      <p>
        Understand that you're covered by <strong>different insurance at different times</strong>:
      </p>
      
      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📅 While Your Car is Rented (Trip Time)</p>
          <p className="mb-2">Turo's protection plan covers your car. This includes:</p>
          <ul className="mb-0">
            <li>Up to $750,000 in third-party liability insurance</li>
            <li>Physical damage reimbursement (minus your deductible)</li>
            <li>This is provided by Travelers Excess and Surplus Lines Company</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🏠 Between Trips (Your Personal Use)</p>
          <p className="mb-2">Your personal insurance covers you when YOU are driving. But you need to ensure they allow carshare activity:</p>
          <ul className="mb-0">
            <li><strong>Option 1:</strong> Call your insurer and ask if they allow Turo/carsharing. Some do with no changes needed.</li>
            <li><strong>Option 2:</strong> Add a rideshare/carshare endorsement (usually $10-30/month extra)</li>
            <li><strong>Option 3:</strong> Switch to an insurer that explicitly permits carsharing (Allstate, Liberty Mutual, USAA, and others have options)</li>
          </ul>
        </div>
      </div>

      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ What to Say When You Call</p>
        <p className="mb-0">"Hi, I'm considering renting my car on Turo, a peer-to-peer carsharing platform. Does my current policy allow this, or do I need to add an endorsement? I want to make sure I'm properly covered when I'm driving my car between rentals."</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 3: Master the Calendar (Working Around Your Job)</h2>
      
      <p>
        If you have a full-time job, scheduling Turo rentals requires strategy. The key is using Turo's calendar and availability settings to only accept trips you can actually fulfill.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Setting Your Availability Hours</h3>
      
      <p>
        In the Turo app, go to <strong>Settings → Trip Preferences → Pickup & Return Hours</strong>. This controls when guests can pick up and drop off.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🕐 Example: 9-to-5 Office Worker</p>
        <ul className="mb-0">
          <li><strong>Weekday Pickup:</strong> 6:00 AM – 7:30 AM (before you leave for work)</li>
          <li><strong>Weekday Return:</strong> 6:30 PM – 9:00 PM (after you're home)</li>
          <li><strong>Weekend:</strong> 8:00 AM – 8:00 PM (flexible all day)</li>
        </ul>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The "Block the Rest of the Day" Technique</p>
        <p className="mb-0">If a guest's trip ends at 10 AM on a Tuesday, you can't do check-in because you're at work. Solution: After that booking, immediately block the rest of the day so no one books a same-day pickup you can't fulfill.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Strategic Trip Buffers</h3>
      
      <p>
        Trip buffers add automatic gaps between bookings. This gives you time to clean and inspect the car.
      </p>
      
      <ul>
        <li><strong>Same-day turnarounds:</strong> Only if you work from home or have flexibility</li>
        <li><strong>1-day buffer:</strong> Recommended for full-time workers—gives you evening to clean</li>
        <li><strong>Weekend-only hosting:</strong> Set buffers to block Monday pickup if you need the car for work</li>
      </ul>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Advance Notice Settings</h3>
      
      <p>
        Set a minimum advance notice (e.g., 24-48 hours) so you're not caught off guard by same-day bookings when you need your car.
      </p>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">📅 Sample Schedule Strategy</p>
        <div className="space-y-2">
          <p><strong>Monday-Thursday:</strong> Block completely (you need your car for commuting)</p>
          <p><strong>Friday evening:</strong> Available for pickup after 6 PM</p>
          <p><strong>Saturday-Sunday:</strong> Available all day</p>
          <p><strong>Monday morning:</strong> Return by 7 AM (or block Monday so trip must end Sunday night)</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 4: Prepare for the Lifestyle Adjustments</h2>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Keep Your Car "Guest Ready"</h3>
      
      <p>
        When you're sharing your personal car, you need to be able to hand it off quickly. This means:
      </p>
      
      <ul>
        <li><strong>No clutter:</strong> Keep personal items minimal or in a bag you can grab quickly</li>
        <li><strong>Regularly clean:</strong> Quick wipe-down between uses, full clean before guests</li>
        <li><strong>Remove valuables:</strong> Nothing you'd be upset to lose should live in the car</li>
        <li><strong>Spare key ready:</strong> Know where it is for back-to-back handoffs</li>
      </ul>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The "Go Bag" Method</p>
        <p className="mb-0">Keep a small bag or container with your personal items that always lives in your car: sunglasses, charging cables, registration, etc. When a rental starts, grab the bag and go. When the rental ends, put it back. Takes 30 seconds.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Emotional Preparation</h3>
      
      <p>
        This is YOUR car. The one you chose, maybe the one you're proud of. And now strangers are going to drive it.
      </p>
      
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Reality Check</p>
        <ul className="mb-0 mt-2">
          <li>Guests will add miles. Some drive more aggressively than you.</li>
          <li>Small dings and scratches will happen.</li>
          <li>The interior will get wear from different driving styles and passengers.</li>
          <li>You might find crumbs, sand, or the occasional mystery stain.</li>
        </ul>
      </div>

      <p>
        If you can accept this as "the cost of doing business," you'll be fine. If every scratch will cause you anxiety, consider waiting until you have a dedicated Turo car.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 5: Alternative Transportation Solutions</h2>
      
      <p>
        Your car is rented. How do you get around?
      </p>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚇 Public Transit</p>
          <p className="mb-0">If you live in a city with decent transit, this is the cheapest option. Your Turo earnings should more than cover a monthly pass.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚗 Partner/Family Car</p>
          <p className="mb-0">If your household has two cars, rent the one you use less frequently and use the other as backup.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚲 Bike or E-Bike</p>
          <p className="mb-0">For short commutes or local errands, biking can work great. An e-bike investment ($500-$2,000) might pay for itself quickly.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚕 Rideshare (Uber/Lyft)</p>
          <p className="mb-0">Use sparingly for emergencies. If you're Ubering every day your car is rented, you're eating into profits significantly.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🏠 Work From Home</p>
          <p className="mb-0">If you have WFH flexibility, schedule rentals on your in-office days when you wouldn't need the car anyway.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Pros and Cons Summary</h2>

      <div className="grid gap-4 my-6 md:grid-cols-2">
        <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4">
          <p className="font-semibold text-green-600 dark:text-green-400 mb-2">✅ Pros</p>
          <ul className="mb-0">
            <li>No upfront car purchase cost</li>
            <li>Low risk—you can stop anytime</li>
            <li>Learn the platform before investing</li>
            <li>Offset your existing car payment</li>
            <li>Test demand in your market</li>
            <li>Gain reviews for future dedicated vehicle</li>
          </ul>
        </div>
        
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="font-semibold text-red-600 dark:text-red-400 mb-2">❌ Cons</p>
          <ul className="mb-0">
            <li>Limited availability (you need it too)</li>
            <li>Wear and tear on YOUR car</li>
            <li>Emotional attachment creates stress</li>
            <li>Lower earnings due to limited rental days</li>
            <li>Lifestyle adjustments required</li>
            <li>Insurance complications to navigate</li>
          </ul>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">When to Get a Dedicated Turo Vehicle</h2>
      
      <p>
        Sharing your personal car is a testing phase. You should consider buying a dedicated Turo car when:
      </p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">✓</span>
          <div>You've hosted successfully for <strong>3-6 months</strong> and understand the business</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">✓</span>
          <div>Your utilization rate is <strong>60%+ on available days</strong></div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">✓</span>
          <div>You have <strong>positive reviews</strong> (4.7+ star average)</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">✓</span>
          <div>You're <strong>turning down bookings</strong> because you need your car</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">✓</span>
          <div>You've handled at least one <strong>damage claim or difficult guest</strong></div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">✓</span>
          <div>You have savings for a <strong>cash purchase or substantial down payment</strong></div>
        </div>
      </div>

      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">The Bottom Line</p>
        <p className="text-center mb-0">
          Starting with your personal car is the <strong>smartest move for a new host</strong>. It lets you learn the ropes with minimal risk. If you hate it, stop—no harm done. If you love it and the numbers work, graduate to a dedicated vehicle with confidence.
        </p>
      </div>
    </div>
  ),

  "buy-your-first-turo-car": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Buying Your First Car</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Strategy Series
      </p>
      
      <p>
        Welcome to one of the most exciting—and terrifying—moments of your Turo journey: <strong>buying your first dedicated fleet vehicle</strong>.
      </p>
      
      <p>
        If you are reading this, you've likely moved past the "testing the waters with my personal car" phase and are ready to treat this as a business. But standing on the dealership lot (or scrolling Facebook Marketplace), you're faced with a strategic fork in the road:
      </p>
      
      <p className="text-center text-lg font-medium my-6">
        Do you buy one shiny, expensive luxury car?<br />
        Or do you buy two (or more) affordable, older economy cars?
      </p>
      
      <p>
        At HostCaptain, we believe in sustainable growth. While the allure of a luxury fleet is strong, for your first business purchase, we strongly recommend the <strong>"Modern Beater" Strategy</strong>.
      </p>
      
      <p>
        Let's break down the analytics of why volume often beats luxury for beginners, based on real data from seasoned hosts.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Case for the "Modern Beater" (Economy Fleet)</h2>
      
      <p>
        When we say "beater," we don't mean a rusted-out junker. Turo has strict eligibility rules (generally vehicles must be under 12 years old and have fewer than 130,000 miles). We are talking about <strong>reliable, depreciated economy cars</strong>—think a 7-to-9-year-old Toyota Corolla.
      </p>
      
      <p>Here is why veteran hosts often prefer running a bunch of these rather than a smaller number of luxury vehicles:</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">1. Risk Diversification</h3>
      
      <p>
        Imagine you have a fixed budget of $200,000 to start your fleet. You essentially have two choices: buy 5 luxury SUVs (at $40k each) or 20 economy sedans (at $10k each).
      </p>
      
      <p>
        If one of your 5 luxury cars gets into an accident and sits in the shop for a month waiting on back-ordered parts, you have just lost <strong>20% of your entire revenue stream</strong>. That is a massive hit to your monthly cash flow that could put your business in the red.
      </p>
      
      <p>
        Now, look at the fleet of 20 cheaper cars. If one is totaled or stuck in the shop, you still have 19 working assets on the road making money. You've only lost <strong>5% of your fleet capacity</strong>.
      </p>
      
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The Takeaway</p>
        <p className="mb-0">Volume insulates you from bad luck. In the rental game, uptime is everything, and a larger, cheaper fleet protects your bottom line from the inevitable accidents.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">2. Emotional Indifference</h3>
      
      <p>
        This is the "sleep at night" factor. When a guest curbs the wheel of your $60,000 Mercedes, it hurts. You feel stress, anger, and anxiety. When a guest curbs the wheel of a $6,000 Hyundai Accent? It's not as big of a deal, especially if you have 10 of them. It's a business asset, not a baby.
      </p>
      
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The Takeaway</p>
        <p className="mb-0">Cheaper cars allow you to emotionally detach from the inevitable wear and tear of rental life, giving you less stress.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">3. The Depreciation Curve</h3>
      
      <p>
        You were right to suspect that cheap cars depreciate less. A brand new car loses a massive chunk of value the moment it drives off the lot. A 2016 Mazda 3 has already taken that hit. Its value has bottomed out, meaning if you sell it in two years, you might get back almost exactly what you paid for it.
      </p>
      
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The Takeaway</p>
        <p className="mb-0">Your "cost of ownership" is significantly lower because the asset holds its residual value.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">4. Cheaper Parts & Maintenance</h3>
      
      <p>
        Economy cars are built to be serviced cheaply. Side mirrors, bumpers, and taillights—the most common casualties of rental damage—are abundant and affordable.
      </p>
      
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The Takeaway</p>
        <p className="mb-0">A cracked bumper on a Corolla might cost $300 to fix. On a luxury SUV, that same crack could involve sensors and calibration costing $2,000+.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Trade-Offs (The "Cons")</h2>
      
      <p>
        To run a sustainable business, you must look at the downsides with eyes wide open. The "Beater" strategy is safer financially, but it is harder logistically.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">1. Higher "Human" Workload</h3>
      
      <p>
        Managing 20 cars means twice as many guest messages, twice as many cleanings, and twice as many check-ins as managing 10 cars.
      </p>
      
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ The Reality</p>
        <p className="mb-0">You will hit your "self-management ceiling" faster. You may need to hire an assistant or a cleaner sooner than a luxury host would.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">2. The Deductible Trap</h3>
      
      <p>
        Many hosts start on the 90 plan (where you keep 90% of earnings, but Turo takes a smaller cut). The trade-off is a high deductible (now $2,750).
      </p>
      
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ The Risk</p>
        <p className="mb-0">If a guest puts a large dent in your cheap car, the repair might cost $1,500. Since this is below your deductible, Turo covers nothing. You have to chase the guest for money, which is difficult and often unsuccessful.</p>
      </div>
      
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ The Fix</p>
        <p className="mb-0">With cheap cars, you must be comfortable ignoring cosmetic damage or learning to buff out scratches yourself.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">3. Maintenance Frequency</h3>
      
      <p>
        While parts are cheaper, older cars do break more often. You need a trusted mechanic on speed dial. You trade "capital risk" (money) for "operational risk" (time/hassle).
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The HostCaptain Recommendation</h2>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">For your first dedicated business car, buy a reliable economy sedan.</p>
        <p className="text-center mb-0">
          Minimize your capital risk while you learn the ropes of the Turo platform, guest communications, and claims processing. Once you have a stable foundation, several cars in your fleet, and a lot more capital to play with, you can start experimenting with luxury cars.
        </p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Recommended Models (The "Gold Standard")</h3>
      
      <p>These vehicles are liquid (easy to sell), reliable, and have consistent demand.</p>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚗 Toyota Corolla (2015–2019)</p>
          <p className="mb-2">The king of reliability. Parts are everywhere.</p>
          <p className="text-sm text-muted-foreground mb-0"><strong>Watch out for:</strong> Avoid 2009-2010 models (oil consumption) and early CVTs. The 2015+ generation is generally bulletproof.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚗 Honda Civic (2013–2015 or 2017+)</p>
          <p className="mb-2">Slightly sportier, great resale value.</p>
          <p className="text-sm text-muted-foreground mb-0"><strong>Watch out for:</strong> The 2016 model year had notable AC compressor and electrical issues. Stick to the late 9th gen (2013-2015) or newer.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚗 Mazda 3 (2014+)</p>
          <p className="mb-0">Often cheaper to buy than Toyota/Honda because it lacks the "brand tax," but offers excellent reliability and a premium feel guests love.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The "Pre-Purchase" Checklist</h2>
      
      <p>Before you hand over the cash, ensure the car fits the Turo "Box":</p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Age Rule:</strong> Is it 12 years old or newer? (As of 2026, the car must be a 2014 model or newer).</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Mileage Rule:</strong> Does it have fewer than 130,000 miles? (Ideally, buy under 100k to give yourself 2-3 years of runway).</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Title:</strong> Is it a Clean Title? (Turo does not accept Branded, Salvage, or Rebuilt titles).</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Recall Check:</strong> Run the VIN. Turo will automatically delist any car with an open safety recall.</div>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Final Thoughts</h2>
      
      <p>
        Your first car is your tuition. It's where you will learn how to handle late returns, smoking fees, and insurance claims. It is much cheaper to learn those lessons on a <strong>$7,600 Corolla</strong> than a <strong>$60,000 Porsche</strong>.
      </p>
      
      <p className="text-xl font-semibold text-center my-8">
        Start small, learn the systems, and then scale. You've got this. 🚀
      </p>
    </div>
  ),

  "how-protection-plans-work": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Understanding Turo Protection Plans</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Getting Started Series
      </p>
      
      <p>
        Protection plans are one of the most confusing—and most important—decisions you'll make as a Turo host. Choose wrong, and a single accident could cost you thousands of dollars or wipe out months of profit.
      </p>
      
      <p>
        This guide breaks down exactly how protection plans work, what each one covers, and which one we recommend for beginner hosts.
      </p>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Disclaimer</p>
        <p className="mb-0">Protection plan details and deductibles can change. We've written this guide based on Turo's official documentation as of 2026. Always verify current terms at <strong>turo.com</strong> before making decisions.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Basics: What Do Protection Plans Cover?</h2>
      
      <p>
        Every Turo protection plan includes two types of coverage:
      </p>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🛡️ Third-Party Liability Insurance</p>
          <p className="mb-2">This covers damage your guest causes to <strong>other people or property</strong> while driving your car. If your guest hits another car or injures a pedestrian, this kicks in.</p>
          <p className="text-sm text-muted-foreground mb-0"><strong>Coverage:</strong> Up to $750,000 (up to $1,250,000 in New York) — same on ALL plans. Provided by Travelers Excess and Surplus Lines Company.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚗 Physical Damage Reimbursement</p>
          <p className="mb-2">This covers damage to <strong>your own car</strong> during a trip—collisions, theft, vandalism, weather damage, etc.</p>
          <p className="text-sm text-muted-foreground mb-0"><strong>Coverage varies by plan:</strong> Different deductibles and additional benefits depending on which plan you choose.</p>
        </div>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Important Clarification</p>
        <p className="mb-0">Physical damage reimbursement is <strong>NOT insurance</strong>—it's a contractual agreement between you and Turo. Turo agrees to reimburse you for damage costs above your deductible, up to $200,000 or your car's actual cash value.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Three Protection Plans (2026)</h2>
      
      <p>
        Turo now offers three plans. The percentage tells you how much of each trip's earnings you keep—the rest goes to Turo for the protection coverage.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted">
              <th className="border p-3 text-left">Plan</th>
              <th className="border p-3 text-left">You Keep</th>
              <th className="border p-3 text-left">Deductible</th>
              <th className="border p-3 text-left">Liability Insurance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-primary/5">
              <td className="border p-3 font-semibold">Max protect ⭐</td>
              <td className="border p-3">70%</td>
              <td className="border p-3 text-green-600 font-semibold">$250</td>
              <td className="border p-3">Up to $750,000</td>
            </tr>
            <tr>
              <td className="border p-3 font-semibold">Balanced</td>
              <td className="border p-3">80%</td>
              <td className="border p-3">$1,500</td>
              <td className="border p-3">Up to $750,000</td>
            </tr>
            <tr className="bg-amber-500/5">
              <td className="border p-3 font-semibold">Max earn</td>
              <td className="border p-3">90%</td>
              <td className="border p-3 text-amber-600 font-semibold">$2,750</td>
              <td className="border p-3">Up to $750,000</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">What Do the Deductibles Mean?</h3>
      
      <p>
        The deductible is your <strong>out-of-pocket cost</strong> before Turo reimburses you for damage. For example, on the Balanced plan with a $1,500 deductible:
      </p>
      
      <ul>
        <li>If damage costs $1,000 to repair → You pay $1,000, Turo pays $0</li>
        <li>If damage costs $2,000 to repair → You pay $1,500, Turo pays $500</li>
        <li>If damage costs $10,000 to repair → You pay $1,500, Turo pays $8,500</li>
      </ul>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ The Deductible Trap for Economy Cars</p>
        <p className="mb-0">If you're hosting a $10,000 economy car on the Max earn plan ($2,750 deductible), most small-to-medium repairs will cost <strong>less than your deductible</strong>. That means Turo pays nothing—you pay everything. A $1,500 fender bender? 100% your cost.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Breaking Down Each Plan</h2>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Max protect: Best Protection ⭐</h3>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-lg p-4 my-4">
        <div className="flex justify-between mb-2">
          <span className="font-semibold">You Keep:</span>
          <span>70% of trip price</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="font-semibold">Turo Takes:</span>
          <span>30% for protection</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="font-semibold">Deductible:</span>
          <span className="text-green-600 font-semibold">$250</span>
        </div>
        <div className="flex justify-between">
          <span className="font-semibold">Liability:</span>
          <span>Up to $750,000</span>
        </div>
      </div>
      
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ HostCaptain's Recommendation for Beginners</p>
        <p className="mb-0">The Max protect plan offers the best balance of protection and manageable risk for new hosts. The $250 deductible is low enough that most repairs won't hurt your bottom line significantly. This is where we recommend starting.</p>
      </div>

      <p><strong>Best for:</strong> New hosts, economy vehicles, anyone who wants peace of mind with a manageable deductible.</p>
      <p><strong>The math:</strong> On a $100/day rental, you get $70. The low deductible makes this ideal for beginners.</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Balanced: Middle Ground</h3>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <div className="flex justify-between mb-2">
          <span className="font-semibold">You Keep:</span>
          <span>80% of trip price</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="font-semibold">Turo Takes:</span>
          <span>20% for protection</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="font-semibold">Deductible:</span>
          <span className="text-amber-600">$1,500</span>
        </div>
        <div className="flex justify-between">
          <span className="font-semibold">Liability:</span>
          <span>Up to $750,000</span>
        </div>
      </div>
      
      <p><strong>Best for:</strong> Experienced hosts with 2-3 vehicles who can absorb occasional $1,500 repair costs and want higher earnings.</p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Max earn: Maximum Earnings, Maximum Risk</h3>
      
      <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-4 my-4">
        <div className="flex justify-between mb-2">
          <span className="font-semibold">You Keep:</span>
          <span>90% of trip price</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="font-semibold">Turo Takes:</span>
          <span>10% for protection</span>
        </div>
        <div className="flex justify-between mb-2">
          <span className="font-semibold">Deductible:</span>
          <span className="text-amber-600 font-semibold">$2,750</span>
        </div>
        <div className="flex justify-between">
          <span className="font-semibold">Liability:</span>
          <span>Up to $750,000</span>
        </div>
      </div>
      
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Not Recommended for Beginners</p>
        <p className="mb-0">The Max earn plan looks attractive (keep 90%!) but that $2,750 deductible means <strong>you're essentially self-insuring</strong> for most damage. One bad incident wipes out months of that extra 20% you kept compared to Max protect.</p>
      </div>
      
      <p><strong>Best for:</strong> Large fleet operators, hosts with commercial insurance, or those with significant cash reserves.</p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">What About Your Personal Insurance?</h2>
      
      <p>
        This is crucial to understand: <strong>Your personal auto insurance likely does NOT cover your car while it's rented on Turo.</strong>
      </p>
      
      <p>
        Most personal policies have a commercial use exclusion. During a Turo trip, your car is covered by Turo's protection plan, not your personal insurance.
      </p>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📅 During Trips (Turo's Coverage)</p>
          <p className="mb-0">Turo's protection plan covers liability and physical damage (minus your deductible). Your personal insurance is NOT active during this time.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🏠 Between Trips (Your Personal Insurance)</p>
          <p className="mb-0">When you're driving your own car for personal use, your personal insurance applies. Make sure your insurer knows you're doing carshare/Turo—some policies require an endorsement.</p>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Important Warning</p>
        <p className="mb-0">If your personal insurer doesn't know about your Turo activity and discovers it during a claim, they could <strong>deny the claim and cancel your policy</strong>. Always disclose your hosting activity to your personal insurer.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Advanced Strategies (For Later)</h2>
      
      <p>
        Some experienced hosts use different protection plans strategically. We'll cover these in detail in our Advanced Protection Techniques guide, but here's a preview:
      </p>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🎓 Max earn for Luxury Vehicles</p>
        <p className="mb-0">Some hosts with expensive luxury cars use the Max earn plan because the high daily rate ($200+/day) makes the 20% savings significant. But they need reserves to cover the $2,750 deductible.</p>
      </div>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🎓 Max earn for Large Fleets</p>
        <p className="mb-0">Hosts with 10+ vehicles sometimes use the Max earn plan because they can "self-insure" across their fleet. One car's damage cost gets absorbed by profits from 9 others.</p>
      </div>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🎓 Commercial Insurance Integration</p>
        <p className="mb-0">Some advanced hosts get commercial auto insurance that covers their vehicles 24/7, allowing them to use the Max earn plan while having backup coverage.</p>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Our Advice</p>
        <p className="mb-0">Don't try advanced strategies until you have at least 6 months of hosting experience, 3+ vehicles, and a solid financial cushion. Start with the Max protect plan and learn the business first.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">How to File a Damage Claim</h2>
      
      <p>
        If your car is damaged during a trip, here's the process:
      </p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">1.</span>
          <div><strong>Document immediately:</strong> Take photos within 24 hours of trip end. Get all angles, close-ups of damage, and wide shots for context.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">2.</span>
          <div><strong>Report in the app:</strong> Go to the trip, tap "Report an issue," and submit your damage report with photos.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">3.</span>
          <div><strong>Wait for response:</strong> Turo's claims team responds within 24 business hours.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">4.</span>
          <div><strong>Get repair estimate:</strong> Turo may use photo-based appraisal (1 day) or require physical inspection (5-7 days).</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">5.</span>
          <div><strong>Complete repairs:</strong> Once approved, get repairs done and submit receipts.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">6.</span>
          <div><strong>Receive reimbursement:</strong> Turo pays repair cost minus your deductible, via Stripe to your bank.</div>
        </div>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The Photo Rule</p>
        <p className="mb-0">Take detailed photos at the <strong>start AND end of every trip</strong>. This is your proof that damage happened during that specific rental. Without before/after photos, getting reimbursement is much harder.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The HostCaptain Recommendation</h2>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">Start with the Max protect Plan</p>
        <p className="text-center mb-4">
          For beginner hosts with one economy car, the Max protect plan offers the best balance:
        </p>
        <ul className="mb-4">
          <li>✅ Manageable $250 deductible</li>
          <li>✅ Keep 70% of earnings</li>
          <li>✅ Up to $750,000 third-party liability coverage</li>
          <li>✅ Low enough risk for sleep-at-night peace of mind</li>
        </ul>
        <p className="text-center text-muted-foreground mb-0">
          Once you have more experience, more vehicles, and more financial cushion, you can experiment with higher-take plans. But don't risk your business learning the hard way.
        </p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Quick Decision Guide</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">👶</span>
          <div><strong>New host, economy car, 1 vehicle:</strong> Max protect plan</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">💎</span>
          <div><strong>New host, luxury/expensive car:</strong> Max protect plan (protect your investment with lowest deductible)</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">📈</span>
          <div><strong>Experienced host, 3+ vehicles, good cash reserves:</strong> Consider Balanced plan</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">🏢</span>
          <div><strong>Large fleet, commercial insurance, business entity:</strong> Consider Max earn plan</div>
        </div>
      </div>
    </div>
  ),

  "how-to-list-your-first-car": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Creating Your First Turo Listing</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Getting Started Series
      </p>
      
      <p>
        Your listing is your storefront. On Turo, guests scroll through dozens of cars—your photos and settings determine whether they click "Book" on your car or keep scrolling.
      </p>
      
      <p>
        This guide walks you through creating an optimized listing step by step, with specific recommendations for each setting.
      </p>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Important Note</p>
        <p className="mb-0">Turo has removed the description field from most listings—<strong>photos are now your ONLY way to communicate with potential guests</strong>. This makes photography even more critical than before.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Before You Start: Preparation Checklist</h2>
      
      <p>
        Make sure you have signed up as a Turo host first (see our "How to Sign Up as a Turo Host" guide). Then gather these items:
      </p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Vehicle information:</strong> License plate, VIN, year/make/model</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Clean car:</strong> Wash and detail your car inside and out before photos</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Camera/phone:</strong> Ensure your phone camera lens is clean</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Good weather:</strong> Pick a sunny or overcast day (no rain, harsh shadows)</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Nice location:</strong> Find a scenic or neutral background</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Time of day:</strong> Plan for golden hour (1 hour after sunrise or 2 hours before sunset)</div>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 1: Take Amazing Photos</h2>
      
      <p>
        Since photos are now your only way to "sell" your car to guests, this is the most important part of your listing. Take time to do this right.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Minimum Required: 10 Photos</h3>
      
      <p>
        Turo requires at least 10 photos, but more is better. Here's exactly what to capture:
      </p>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📸 Exterior Shots (6 minimum)</p>
          <ul className="mb-0">
            <li><strong>Front 3/4 angle:</strong> This is your "hero shot"—what guests see first. Stand at the front corner, showing both the front and side.</li>
            <li><strong>Rear 3/4 angle:</strong> Same concept, from the back corner</li>
            <li><strong>Direct front:</strong> Head-on front view</li>
            <li><strong>Direct rear:</strong> Head-on back view</li>
            <li><strong>Driver's side profile:</strong> Full side view</li>
            <li><strong>Passenger's side profile:</strong> Full side view from other side</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📸 Interior Shots (4 minimum)</p>
          <ul className="mb-0">
            <li><strong>From driver's door:</strong> Show dashboard, steering wheel, and front seats</li>
            <li><strong>Dashboard close-up:</strong> Turn on accessory mode so screens light up</li>
            <li><strong>Back seats:</strong> Shot from front seats looking back</li>
            <li><strong>Trunk/cargo space:</strong> Show how much room there is</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📸 Bonus Shots (Highly Recommended)</p>
          <ul className="mb-0">
            <li>Special features: sunroof open, heated seat buttons, Apple CarPlay screen</li>
            <li>Unique angles: wheel/rim close-up, badge close-up</li>
            <li>Lifestyle shots: car in a scenic location</li>
          </ul>
        </div>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Photography Best Practices</h3>

      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ Do This</p>
        <ul className="mb-0 mt-2">
          <li><strong>Shoot during golden hour:</strong> 1 hour after sunrise or 2 hours before sunset gives soft, warm light</li>
          <li><strong>Step back 5 paces:</strong> Turo crops 20% from all sides—give yourself margin</li>
          <li><strong>Clean your lens:</strong> A smudged phone camera ruins photos</li>
          <li><strong>Hold steady:</strong> Tuck your elbows against your body, hold your breath while shooting</li>
          <li><strong>Keep horizon level:</strong> Nothing looks more amateur than a tilted photo</li>
          <li><strong>Turn on running lights:</strong> Makes the car look more alive in exterior shots</li>
          <li><strong>Choose a clean background:</strong> Parks, scenic roads, or plain walls work well</li>
        </ul>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Avoid This</p>
        <ul className="mb-0 mt-2">
          <li><strong>Midday sun (10 AM - 3 PM):</strong> Creates harsh shadows and washed-out colors</li>
          <li><strong>Flash photography:</strong> Looks unnatural, creates reflections</li>
          <li><strong>Cluttered backgrounds:</strong> Garbage cans, other cars, messy garages</li>
          <li><strong>Wide-angle distortion:</strong> Don't zoom—physically move closer or farther</li>
          <li><strong>Dirty car:</strong> Even small dust shows in photos</li>
          <li><strong>Personal items visible:</strong> Remove everything from interior</li>
        </ul>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Free Professional Photos</p>
        <p className="mb-0">In many markets, Turo offers <strong>free professional photography</strong>. Check if it's available in your area—it's a massive advantage. Professional photos can increase bookings significantly.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 2: Start Your Listing in the App</h2>
      
      <p>
        Open the Turo app and tap <strong>"List your car"</strong> or go to the <strong>Vehicles</strong> tab and tap the <strong>+</strong> button.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Enter Vehicle Information</h3>
      
      <p>
        Turo will ask for your license plate number first. Enter it exactly as it appears on your plate. Turo uses this to pull your vehicle's information automatically.
      </p>
      
      <p>
        If the automatic lookup doesn't work, you can enter your VIN (found on your registration or inside the driver's door frame) or manually select year, make, and model.
      </p>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Double-Check the Details</p>
        <p className="mb-0">Make sure Turo pulled the correct trim level and features. If your car has navigation, leather seats, or other upgrades, ensure they're reflected. Guests compare features when deciding which car to book.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 3: Upload Your Photos</h2>
      
      <p>
        Add all the photos you took. Turo will guide you through which angles are required.
      </p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">📌</span>
          <div><strong>Set your best photo as the cover:</strong> The front 3/4 angle usually works best as the first image guests see.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">📌</span>
          <div><strong>Order matters:</strong> Exterior shots first, then interior, then special features.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">📌</span>
          <div><strong>More is better:</strong> 15-20 photos help guests feel confident about what they're booking.</div>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 4: Set Your Vehicle Features</h2>
      
      <p>
        Turo will show a list of features. Check everything that applies to your car:
      </p>
      
      <div className="grid gap-4 my-6 md:grid-cols-2">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold mb-2">Popular Features to Highlight</p>
          <ul className="mb-0 text-sm">
            <li>Bluetooth</li>
            <li>Apple CarPlay / Android Auto</li>
            <li>Backup camera</li>
            <li>Navigation</li>
            <li>Heated seats</li>
            <li>Sunroof/moonroof</li>
            <li>Leather seats</li>
            <li>USB charger</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold mb-2">Practical Features</p>
          <ul className="mb-0 text-sm">
            <li>Toll pass (E-ZPass, FasTrak, etc.)</li>
            <li>Child seat (if you provide one)</li>
            <li>Bike rack</li>
            <li>Ski rack</li>
            <li>All-wheel drive</li>
            <li>Keyless entry</li>
          </ul>
        </div>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Be Accurate</p>
        <p className="mb-0">Only check features your car actually has. Guests will leave bad reviews if they expected navigation and it's not there. Accuracy builds trust.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 5: Configure Trip Settings</h2>
      
      <p>
        These settings control how and when your car can be booked.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Advance Notice</h3>
      
      <p>
        How much warning do you need before a trip starts? Options typically range from same-day to several days.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold mb-2">🎯 Recommendation for New Hosts</p>
        <p className="mb-0"><strong>12-24 hours advance notice.</strong> This gives you time to prepare the car without missing last-minute bookings. As you get more experienced, you can reduce this to capture same-day rentals.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Trip Duration</h3>
      
      <p>
        Set minimum and maximum trip lengths.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold mb-2">🎯 Recommendation for New Hosts</p>
        <ul className="mb-0">
          <li><strong>Minimum:</strong> 1 day (captures short trips)</li>
          <li><strong>Maximum:</strong> 30 days (or shorter if you need regular access to your car)</li>
        </ul>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Pickup & Return Hours</h3>
      
      <p>
        When can guests pick up and drop off?
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold mb-2">🎯 Recommendation for New Hosts</p>
        <ul className="mb-0">
          <li><strong>Weekdays:</strong> 8 AM - 8 PM (or adjust based on your work schedule)</li>
          <li><strong>Weekends:</strong> 8 AM - 8 PM (more flexibility on weekends)</li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">Wider hours = more bookings, but only offer hours you can actually accommodate.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Mileage Limit</h3>
      
      <p>
        How many miles can the guest drive per day?
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold mb-2">🎯 Recommendation for New Hosts</p>
        <p className="mb-0"><strong>200 miles/day</strong> is a good starting point. This is enough for most local use and day trips. Guests can pay for additional miles if needed.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 6: Choose Your Protection Plan</h2>
      
      <p>
        Select the protection plan for this vehicle. If you haven't read our "How Protection Plans Work" guide yet, do that first!
      </p>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">Our Recommendation: 75 Plan</p>
        <p className="text-center mb-0">
          The 75 Plan offers the best balance of protection ($250 deductible) and earnings (keep 75%) for new hosts. Start here and adjust later once you have experience.
        </p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 7: Set Your Pricing</h2>
      
      <p>
        This is where many new hosts stress out. Here's how to approach it:
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Use Dynamic Pricing (Recommended)</h3>
      
      <p>
        In the US, Canada, and UK, Turo offers <strong>dynamic pricing</strong> that automatically adjusts your rate based on:
      </p>
      
      <ul>
        <li>Market demand in your area</li>
        <li>Time of year (seasonality)</li>
        <li>Day of week</li>
        <li>Local events</li>
        <li>Your vehicle's performance</li>
      </ul>

      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ Our Recommendation</p>
        <p className="mb-0"><strong>Start with dynamic pricing enabled.</strong> Turo's algorithm is pretty good at finding the right price. You can always override it later for specific dates.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Initial Pricing Strategy for New Hosts</h3>
      
      <p>
        When you're brand new with zero reviews, guests are taking a chance on you. Make it worth their while:
      </p>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🚀 The "Launch Special" Strategy</p>
        <ol className="mb-0">
          <li><strong>Week 1-2:</strong> Price 20% below similar cars in your area</li>
          <li><strong>After first 5-star review:</strong> Raise to 10% below market</li>
          <li><strong>After 5+ reviews:</strong> Match market rates</li>
          <li><strong>After 10+ reviews with 4.8+ rating:</strong> Test pricing above market</li>
        </ol>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 How to Research Market Rates</p>
        <p className="mb-0">Search Turo as a guest in your area. Filter for similar vehicles (same year range, same class). Look at hosts with good reviews—that's your target rate once you build credibility.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Set Up Discounts</h3>
      
      <p>
        Turo lets you offer discounts for longer trips and advance bookings:
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold mb-2">🎯 Recommended Discount Settings</p>
        <ul className="mb-0">
          <li><strong>Weekly discount:</strong> 10-15% off for 7+ day trips</li>
          <li><strong>Monthly discount:</strong> 25-30% off for 30+ day trips</li>
          <li><strong>Advance booking discount:</strong> 5% off for bookings made 7+ days ahead</li>
        </ul>
        <p className="text-sm text-muted-foreground mt-2">Longer trips mean fewer cleanings, fewer check-ins, and guaranteed income. The discount is worth it.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Step 8: Review and Publish</h2>
      
      <p>
        Before hitting publish, review everything one more time:
      </p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Photos:</strong> Are they high-quality? Did you upload at least 10?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Vehicle info:</strong> Is the year, make, model, and trim correct?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Features:</strong> Did you check all applicable features?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Protection plan:</strong> Is it set to 75% (or your choice)?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Pricing:</strong> Does dynamic pricing show a reasonable rate?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Availability:</strong> Is your calendar set correctly?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Trip settings:</strong> Are advance notice and pickup hours workable?</div>
        </div>
      </div>
      
      <p>
        If everything looks good, tap <strong>Publish</strong>. Your listing is now live!
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">After Publishing: Next Steps</h2>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📱 Enable Notifications</p>
          <p className="mb-0">Make sure Turo app notifications are turned on. Fast response times (within 1 hour) dramatically improve your booking rate.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🔍 View Your Listing as a Guest</p>
          <p className="mb-0">Search for your car on Turo (logged out or in incognito mode) to see exactly what guests see. Does it look good compared to competitors?</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📅 Block Any Unavailable Dates</p>
          <p className="mb-0">If you need your car on specific days, block them now before bookings come in. Canceling bookings hurts your account.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">⏰ Prepare for Your First Booking</p>
          <p className="mb-0">Read our "My First Rental! What to Do" guide so you're ready when that first booking notification arrives. (It's exciting and nerve-wracking!)</p>
        </div>
      </div>

      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">Congratulations! 🎉</p>
        <p className="text-center mb-0">
          Your car is now listed on Turo. Bookings may come within hours or take a few days depending on your market. Stay patient, keep notifications on, and respond quickly when inquiries come in!
        </p>
      </div>
    </div>
  ),

  "checkin-checkout-process": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Mastering the Check-in/Check-out Process</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Getting Started Series
      </p>
      
      <p>
        The check-in and check-out process is where most damage disputes, misunderstandings, and bad reviews happen. Get this right, and you'll protect yourself financially AND earn 5-star reviews.
      </p>
      
      <p>
        This guide covers exactly how to handle guest handoffs—whether you meet in person or use remote/contactless check-in.
      </p>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The #1 Rule of Hosting</p>
        <p className="mb-0"><strong>Documentation protects you.</strong> Every photo you take, every message you send through the app—it all creates a record that protects you if something goes wrong. Never skip the photos, even for "nice" guests.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Part 1: Before the Trip</h2>
      
      <p>
        Preparation starts 24-48 hours before the guest arrives. Here's your pre-trip checklist:
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">24-48 Hours Before</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Send a welcome message:</strong> Confirm pickup time and location. Introduce yourself briefly.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Check guest's license status:</strong> Turo will prompt you if there are issues with their verification.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Review the guest's profile:</strong> Check their rating and any host reviews about them.</div>
        </div>
      </div>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">✉️ Sample Welcome Message</p>
        <p className="italic text-muted-foreground mb-0">"Hi [Name]! I'm looking forward to your trip. Just confirming we're meeting at [location] at [time] on [date]. I'll be in a blue shirt so you can spot me easily. The car will be freshly cleaned and ready to go. Let me know if you have any questions before then! - [Your name]"</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Day Before / Morning Of</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Clean the car:</strong> Full interior wipe-down, vacuum, windows, and exterior wash</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Fill the gas tank:</strong> Fill to full (or whatever level you expect returned)</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Check tire pressure:</strong> Ensure all tires are properly inflated</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Check fluid levels:</strong> Oil, windshield washer fluid, coolant</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Remove personal items:</strong> Check glove box, center console, door pockets, trunk</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Test everything:</strong> Lights, AC, radio, windows, locks all working</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Note the odometer:</strong> Write down or photograph the current mileage</div>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Part 2: The Check-in Process</h2>
      
      <p>
        When your guest arrives, you have two options: <strong>in-person handoff</strong> or <strong>remote/contactless check-in</strong>.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Option A: In-Person Check-in (Recommended for New Hosts)</h3>
      
      <p>
        Meeting guests in person helps build rapport, answer questions, and ensure proper documentation. Here's the step-by-step process:
      </p>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 1: Verify Identity</p>
          <p className="mb-2">Ask to see their <strong>physical driver's license</strong> (not a photo or digital version).</p>
          <ul className="mb-0 text-sm">
            <li>Confirm the name matches the booking</li>
            <li>Check that the photo matches the person in front of you</li>
            <li>Verify the license isn't expired</li>
            <li>Complete the "Confirm License" step in the Turo app</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 2: Walk-Around Together</p>
          <p className="mb-2">Walk around the car with your guest and point out any existing wear:</p>
          <ul className="mb-0 text-sm">
            <li>"This scratch on the bumper was here before your trip"</li>
            <li>"There's a small ding on this door—just want you to know about it"</li>
            <li>Show them you're documenting these in your photos</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 3: Take Check-in Photos (CRITICAL)</p>
          <p className="mb-2">Use the Turo app to take photos. You need at minimum:</p>
          <ul className="mb-0 text-sm">
            <li><strong>15 exterior photos:</strong> All sides, corners, bumpers, wheels</li>
            <li><strong>8 interior photos:</strong> Dashboard, seats, floors, trunk</li>
            <li><strong>Odometer:</strong> Clear shot of current mileage</li>
            <li><strong>Fuel gauge:</strong> Clear shot showing fuel level</li>
            <li><strong>Any existing damage:</strong> Close-ups of scratches, dents, stains</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 4: Car Orientation</p>
          <p className="mb-2">Show your guest how to operate the car:</p>
          <ul className="mb-0 text-sm">
            <li>How to start the car (especially for push-button or quirky ignitions)</li>
            <li>Where the gas cap release is</li>
            <li>How to use the infotainment/navigation system</li>
            <li>Any quirks ("The AC takes a minute to get cold" or "Turn the key gently")</li>
            <li>Where documents (registration, insurance) are located</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 5: Set Expectations</p>
          <p className="mb-0">Remind them of key policies: no smoking, return with same fuel level, mileage limit, and who to contact if there's an emergency.</p>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ The 24-Hour Photo Rule</p>
        <p className="mb-0">Check-in photos must be taken and uploaded through the Turo app <strong>within 24 hours of the trip start time</strong>. Without these photos, you may not be eligible for damage reimbursement if something happens.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Option B: Remote/Contactless Check-in</h3>
      
      <p>
        If you can't meet in person, remote check-in is the alternative. This requires more preparation:
      </p>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🔑 Remote Check-in Requirements</p>
        <ul className="mb-0">
          <li><strong>Turo Go or key lockbox:</strong> A way for the guest to access keys without you present</li>
          <li><strong>Detailed instructions:</strong> Send clear directions for where to find the car and keys</li>
          <li><strong>Pre-trip photos:</strong> Take and upload all check-in photos before the guest arrives</li>
          <li><strong>Clear communication:</strong> Be available by phone/text during the check-in window</li>
        </ul>
      </div>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">✉️ Sample Remote Check-in Message</p>
        <p className="italic text-muted-foreground mb-0">"Hi [Name]! The car is parked at [exact location]. The key is in a lockbox on the driver's side rear wheel well—the code is [XXXX]. Registration and insurance are in the glove box. Take a quick walk around and snap your check-in photos in the app before you drive off. The gas is full, please return it full. Text me if you have any trouble finding it or have questions! Safe travels!"</p>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Turo Go</p>
        <p className="mb-0">If your car is compatible, <strong>Turo Go</strong> lets guests unlock and start the car using the Turo app—no physical key exchange needed. This is the best option for remote check-in if available for your vehicle.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Part 3: During the Trip</h2>
      
      <p>
        Once the guest drives away, your job is mostly done—but stay accessible.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Best Practices During the Trip</h3>
      
      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📱 Stay Responsive</p>
          <p className="mb-0">Keep your phone on and check messages regularly. If the guest has a question or problem, quick responses make a big difference.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">✉️ Mid-Trip Check-in (Optional)</p>
          <p className="mb-0">For multi-day trips, a quick "Hope everything is going great!" message on day 2 or 3 shows you care. Don't overdo it—one message is enough.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">🚨 If Something Goes Wrong</p>
          <p className="mb-0">If the guest reports an accident, breakdown, or other issue, guide them to contact Turo's roadside assistance (available 24/7 in the app) and work through the proper channels.</p>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Don't Hover</p>
        <p className="mb-0">Some hosts obsessively track their car's GPS or message guests constantly. This makes guests uncomfortable and can lead to bad reviews. Trust your guest unless you have a specific reason not to.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Part 4: The Check-out Process</h2>
      
      <p>
        Check-out is just as important as check-in—this is when you document the car's return condition and catch any damage.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">When the Guest Returns</h3>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 1: Greet Them</p>
          <p className="mb-0">Thank them for the trip. Ask how everything went—any issues or feedback?</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 2: Walk-Around Inspection</p>
          <p className="mb-2">Walk around the car together and inspect:</p>
          <ul className="mb-0 text-sm">
            <li>All exterior panels, bumpers, mirrors, wheels</li>
            <li>Interior seats, floors, dashboard</li>
            <li>Trunk and cargo area</li>
            <li>Fuel level (should match check-in)</li>
            <li>Overall cleanliness</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 3: Take Check-out Photos (CRITICAL)</p>
          <p className="mb-2">Use the Turo app to take photos from the <strong>same angles as check-in</strong>:</p>
          <ul className="mb-0 text-sm">
            <li><strong>15 exterior photos:</strong> Match your check-in angles exactly</li>
            <li><strong>8 interior photos:</strong> Same spots as before</li>
            <li><strong>Odometer:</strong> Document final mileage</li>
            <li><strong>Fuel gauge:</strong> Document return fuel level</li>
            <li><strong>Any NEW damage:</strong> Close-ups from multiple angles</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 4: End the Trip in the App</p>
          <p className="mb-0">Complete the check-out process in Turo. This officially ends the trip and starts your timeline for reporting any issues.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Step 5: Thank and Request Review</p>
          <p className="mb-0">"Thanks so much for taking great care of the car! If you have a moment, I'd really appreciate a review on Turo. Safe travels!"</p>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ The 24-Hour Damage Reporting Window</p>
        <p className="mb-0">If you discover damage, you have <strong>24 hours after trip end</strong> to report it through Turo. After 24 hours, you may lose your ability to file a claim. Don't delay—report immediately even if you're not sure about the severity.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Part 5: If You Find Damage</h2>
      
      <p>
        Despite your best efforts, damage happens. Here's what to do:
      </p>

      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">1.</span>
          <div><strong>Stay calm and professional.</strong> Don't accuse the guest or get upset. Just document.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">2.</span>
          <div><strong>Take detailed photos.</strong> Get wide shots showing context and close-ups showing damage size (use a ruler or coin for scale).</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">3.</span>
          <div><strong>Compare to check-in photos.</strong> Confirm this damage is new.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">4.</span>
          <div><strong>Report in the Turo app immediately.</strong> Go to the trip → "Report an issue" → follow the prompts.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg font-bold">5.</span>
          <div><strong>Wait for Turo's response.</strong> They'll review your claim and compare photos. Respond promptly to any requests for more information.</div>
        </div>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The Power of Good Documentation</p>
        <p className="mb-0">Hosts who take thorough check-in/check-out photos almost always win damage disputes. Hosts who skip photos often lose. The 5 minutes of photo-taking can save you thousands of dollars.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Photo Checklist Reference</h2>
      
      <p>
        Print this out or screenshot it to use at every check-in and check-out:
      </p>

      <div className="grid gap-4 my-6 md:grid-cols-2">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold mb-2">📸 Exterior (15 photos)</p>
          <ul className="mb-0 text-sm">
            <li>☐ Front (straight on)</li>
            <li>☐ Front 3/4 left</li>
            <li>☐ Front 3/4 right</li>
            <li>☐ Driver's side (full)</li>
            <li>☐ Passenger's side (full)</li>
            <li>☐ Rear (straight on)</li>
            <li>☐ Rear 3/4 left</li>
            <li>☐ Rear 3/4 right</li>
            <li>☐ Front bumper close-up</li>
            <li>☐ Rear bumper close-up</li>
            <li>☐ All 4 wheels/tires</li>
            <li>☐ Roof (if visible damage)</li>
            <li>☐ Any existing damage (close-up)</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold mb-2">📸 Interior (8 photos)</p>
          <ul className="mb-0 text-sm">
            <li>☐ Dashboard/steering wheel</li>
            <li>☐ Center console</li>
            <li>☐ Driver's seat</li>
            <li>☐ Passenger's seat</li>
            <li>☐ Back seats</li>
            <li>☐ Floor mats/carpet</li>
            <li>☐ Trunk/cargo area</li>
            <li>☐ Odometer (current mileage)</li>
            <li>☐ Fuel gauge</li>
          </ul>
        </div>
      </div>

      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">The Golden Rule</p>
        <p className="text-center mb-0">
          <strong>Take your photos at check-in and check-out. Every. Single. Time.</strong> Even for repeat guests, even when you're in a hurry, even when it seems unnecessary. The one time you skip it will be the time you wish you hadn't.
        </p>
      </div>
    </div>
  ),

  "my-first-rental": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Your First Rental is Here!</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Getting Started Series
      </p>
      
      <p>
        Congratulations! 🎉 You just got your first booking notification. Your heart is probably racing. Maybe your hands are a little sweaty. That's totally normal.
      </p>
      
      <p>
        This guide walks you through exactly what to do from the moment you see that booking notification to the moment you wave goodbye to your first guest—and hopefully get your first 5-star review.
      </p>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Take a Deep Breath</p>
        <p className="mb-0">Every successful Turo host started exactly where you are right now. The fact that you're reading this guide means you're more prepared than most new hosts. You've got this!</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Stage 1: You Got the Notification!</h2>
      
      <p>
        A booking just came in. Here's what's happening and what you need to do immediately:
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Understand What Just Happened</h3>
      
      <p>
        If you have "Instant Book" enabled (most hosts do), the booking is already confirmed. The guest has paid, and you're committed to providing the car at the agreed time.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">📱 Your Booking Notification Shows:</p>
        <ul className="mb-0">
          <li><strong>Guest name:</strong> Who's renting your car</li>
          <li><strong>Trip dates:</strong> When they need the car</li>
          <li><strong>Pickup time:</strong> When to meet</li>
          <li><strong>Pickup location:</strong> Where to meet</li>
          <li><strong>Total payout:</strong> What you'll earn (after Turo's cut)</li>
        </ul>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Immediate Actions (Within 1 Hour)</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Check your calendar:</strong> Make sure you can actually fulfill this booking. Is the car available? Are YOU available for the handoff?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Review the guest's profile:</strong> Tap their name to see their rating, reviews from other hosts, and how long they've been on Turo.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Send a welcome message:</strong> Introduce yourself and confirm the details. (See template below)</div>
        </div>
      </div>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">✉️ First Response Template</p>
        <p className="italic text-muted-foreground mb-0">"Hi [Guest Name]! Thanks for booking my [Car Model]. I'm excited to host you! Just confirming: we'll meet at [Location] on [Date] at [Time]. The car will be freshly cleaned with a full tank of gas. If you have any questions before the trip, feel free to message me anytime. Looking forward to it! - [Your Name]"</p>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Why Fast Response Matters</p>
        <p className="mb-0">Turo tracks your response time. Fast responses (within 1 hour) improve your visibility in search results and make guests feel confident. Slow responses make guests nervous—they might cancel and book with someone else.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Stage 2: Preparing Your Car</h2>
      
      <p>
        This is your chance to make a great first impression. Your car's condition directly impacts your review.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">The Day Before</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Wash the exterior:</strong> Go through a car wash or hand wash. First impressions matter!</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Vacuum the interior:</strong> Floors, seats, between seats, trunk.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Wipe all surfaces:</strong> Dashboard, steering wheel, center console, door handles.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Clean the windows:</strong> Inside and outside—streaky windows look bad.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Check for odors:</strong> Air out the car if needed. No one wants a smelly rental.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Remove ALL personal items:</strong> Check glove box, door pockets, center console, under seats, trunk.</div>
        </div>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Morning Of (Or Night Before)</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Fill the gas tank:</strong> To full, or to your standard level (whatever you expect back).</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Check tire pressure:</strong> Properly inflated tires are safer and get better mileage.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Check fluids:</strong> Oil level, windshield washer fluid.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Test everything:</strong> Lights, AC/heat, radio, windows, locks, wipers.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Note the odometer:</strong> Write down or photograph the mileage.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Confirm documents are in car:</strong> Registration and insurance card in glove box.</div>
        </div>
      </div>

      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ The "Hotel Room" Standard</p>
        <p className="mb-0">Think of your car like a hotel room. When guests check in, they expect it to be spotless—as if no one has ever used it before. That's the standard you're aiming for.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Stage 3: The Handoff (Meeting Your Guest)</h2>
      
      <p>
        This is the moment! You're about to meet your first guest. Here's how to handle it like a pro:
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Before You Meet</h3>
      
      <ul>
        <li><strong>Arrive 5-10 minutes early:</strong> Give yourself time to park and take check-in photos before the guest arrives.</li>
        <li><strong>Take your check-in photos:</strong> All exterior angles, interior, odometer, fuel gauge. Do this BEFORE the guest arrives.</li>
        <li><strong>Have your phone ready:</strong> Turo app open, ready to verify license and complete check-in.</li>
      </ul>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">When the Guest Arrives</h3>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">1. Greet Warmly</p>
          <p className="mb-0">"Hi [Name]! Great to meet you. I'm [Your name]. Welcome to [City]!" A smile and friendly handshake set the tone.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">2. Verify Their License</p>
          <p className="mb-0">Ask to see their physical driver's license. Open the Turo app and complete the license verification. Check that the name and face match.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">3. Walk Around Together</p>
          <p className="mb-0">Point out any existing wear: "There's a small scratch here—just want you to know it was here before." This protects both of you.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">4. Show Them the Car</p>
          <p className="mb-2">Give a quick orientation:</p>
          <ul className="mb-0 text-sm">
            <li>How to start the car (especially if push-button or unfamiliar)</li>
            <li>Where the gas cap release is</li>
            <li>How to connect Bluetooth/CarPlay if applicable</li>
            <li>Any quirks: "The AC takes a minute" or "The trunk latch is tricky"</li>
            <li>Where documents are (glove box)</li>
          </ul>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">5. Remind Them of the Basics</p>
          <p className="mb-0">"Please return it with the same fuel level, no smoking inside, and message me if you have any questions during the trip!"</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">6. Hand Over the Keys</p>
          <p className="mb-0">"You're all set! Have a great trip!" Wave goodbye and let them go.</p>
        </div>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Managing First-Time Nerves</p>
        <p className="mb-0">Feeling nervous is normal! Remember: the guest is probably a little nervous too (new city, new car). Being friendly and calm puts both of you at ease. Take a deep breath and be yourself.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Stage 4: During the Trip</h2>
      
      <p>
        Your guest has driven away. Now what?
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">What to Do</h3>
      
      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">📱 Stay Available</p>
          <p className="mb-0">Keep your phone nearby and check messages. If they have a question or problem, quick responses help.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">😌 Relax (Mostly)</p>
          <p className="mb-0">It's normal to feel anxious your first time. But most trips go perfectly fine. Your car is in good hands.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">✉️ Optional Mid-Trip Check-in</p>
          <p className="mb-0">For trips longer than 2 days, a quick "Hope everything's going great!" message is friendly. Don't overdo it though—one message is enough.</p>
        </div>
      </div>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ If Something Goes Wrong</p>
        <p className="mb-0">If the guest reports an accident, breakdown, or other issue: Stay calm. Guide them to use Turo's roadside assistance (24/7 in the app). Turo handles most emergencies—you don't have to solve everything yourself.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Stage 5: Getting Your Car Back</h2>
      
      <p>
        The trip is ending. Time to get your car back and document its condition.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">When the Guest Returns</h3>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">1. Greet Them</p>
          <p className="mb-0">"Hey, welcome back! How was the trip?" A friendly greeting sets a positive tone for the review they're about to write.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">2. Walk Around Together</p>
          <p className="mb-0">Inspect the car with them present. Check all sides, bumpers, wheels, and interior.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">3. Take Check-out Photos</p>
          <p className="mb-0">Same angles as check-in: all exterior sides, interior, odometer, fuel gauge. Upload through the Turo app.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">4. Complete the Trip in the App</p>
          <p className="mb-0">End the trip in Turo. This starts the clock for damage reporting (24 hours) and unlocks your earnings.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">5. Thank Them and Ask for Review</p>
          <p className="mb-0">"Thanks so much for taking care of the car! If you have a moment, I'd really appreciate a review. Safe travels!"</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Stage 6: After the Trip</h2>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Inspect More Carefully</h3>
      
      <p>
        Once the guest leaves, do a more thorough inspection:
      </p>
      
      <ul>
        <li>Check under seats for forgotten items or trash</li>
        <li>Inspect trunk thoroughly</li>
        <li>Look for any damage you might have missed</li>
        <li>Check fuel level matches what they returned</li>
        <li>Smell for smoke (even if you have a no-smoking policy)</li>
      </ul>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Leave a Review</h3>
      
      <p>
        Turo will prompt you to review your guest. Be honest but fair:
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">✍️ Sample Positive Review</p>
        <p className="italic text-muted-foreground mb-0">"[Name] was a great guest! Returned the car on time, clean, and with a full tank. Communication was easy. Would happily host again!"</p>
      </div>

      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Reviews Are a Two-Way Street</p>
        <p className="mb-0">When you leave a fair, thoughtful review for your guest, they're more likely to leave one for you. Your first 5-star review is hugely important for getting future bookings!</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Common First-Trip Worries (And Why They're Okay)</h2>

      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">😰 "What if they damage my car?"</p>
          <p className="mb-0">This is why you have protection plans and take photos. Most guests are careful. And if something does happen, you have documentation and Turo's claims process.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">😰 "What if they don't show up?"</p>
          <p className="mb-0">Rare, but it happens. Wait the full grace period, document that they were a no-show, and contact Turo. You may still get paid for the booking.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">😰 "What if they return it late?"</p>
          <p className="mb-0">Guests who return late are charged automatically. Communicate with them if they're running behind. Most delays are just traffic or flight delays.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">😰 "What if they leave a bad review?"</p>
          <p className="mb-0">Focus on what you can control: clean car, friendly service, clear communication. Bad reviews happen occasionally, but one bad review among many good ones won't hurt you.</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Celebrate Your First Rental! 🎉</h2>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">You did it!</p>
        <p className="text-center mb-4">
          Your first rental is complete. You survived. Your car survived. You probably even got paid and maybe got a 5-star review.
        </p>
        <p className="text-center mb-4">
          The first one is the hardest because everything is new. The second rental will be easier. By your fifth rental, you'll wonder why you were ever nervous.
        </p>
        <p className="text-center text-lg font-semibold mb-0">
          Welcome to Turo hosting! 🚗✨
        </p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">What's Next?</h3>
      
      <ul>
        <li>Keep notifications on and respond quickly to future bookings</li>
        <li>Build up your reviews—each one makes the next booking easier to get</li>
        <li>Fine-tune your pricing based on demand</li>
        <li>Consider expanding to a second car once you're consistently booked</li>
      </ul>
    </div>
  ),
  "will-you-have-any-renters": (
    <div className="prose prose-slate dark:prose-invert max-w-none">
      <h2 className="text-2xl font-bold">Will You Have Any Renters?</h2>
      <p className="text-lg italic text-muted-foreground border-l-4 border-primary pl-4 my-6">
        The HostCaptain Launch Kit: Getting Started Series
      </p>
      
      <p>
        If you're thinking about listing your car on Turo, you've probably had this thought: <strong>"What if nobody rents my car?"</strong> It's a completely valid concern—especially if you're investing time setting up a listing or considering buying a car specifically for Turo.
      </p>
      
      <p>
        Good news: <strong>The demand is real, and new hosts do get bookings.</strong> Let's break down what you can realistically expect, how Turo's platform actually helps new hosts, and what you can do to maximize your chances of getting booked quickly.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Short Answer: Yes, You Will Get Renters</h2>
      
      <p>
        Turo has <strong>millions of active guests</strong> searching for cars every day. The platform handles over <strong>$1 billion in bookings annually</strong>. Unless you're in an extremely remote area with no airports, colleges, or population centers, there's almost certainly demand for rentals near you.
      </p>
      
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 The Real Question</p>
        <p className="mb-0">It's not <em>whether</em> you'll get renters—it's <em>how quickly</em> and <em>how consistently</em>. That comes down to your pricing, photos, location, and the type of car you're listing.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Turo Actually Helps New Hosts Get Started</h2>
      
      <p>
        Here's something most new hosts don't realize: <strong>Turo wants you to succeed</strong>. A host who quits after getting zero bookings is bad for their business. So they've built features that actually give new listings a boost.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">The "New Listing" Visibility Boost</h3>
      
      <p>
        Turo's search algorithm uses machine learning to rank vehicles. While they don't publish exact details, multiple factors suggest new listings get temporary visibility advantages:
      </p>
      
      <ul>
        <li><strong>Fresh listings appear in search</strong> even without reviews, ratings, or trip history</li>
        <li><strong>Guests filtering by "New Hosts"</strong> can specifically find you</li>
        <li><strong>Competitive pricing signals</strong> on new listings can push you higher in results</li>
      </ul>
      
      <p>
        This "honeymoon period" doesn't last forever. Once you've been listed for a few weeks, your ranking becomes more dependent on your actual performance (reviews, response time, acceptance rate). But it does mean <strong>you're not starting from zero visibility</strong>.
      </p>

      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">🎯 The Goal</p>
        <p className="mb-0">Use your early visibility window to get your first 1-3 bookings and reviews. Those reviews compound—each good review makes it easier to get the next booking.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">How Long Until Your First Booking?</h2>
      
      <p>
        This varies wildly based on your market, car type, and pricing strategy. But here's what most new hosts experience:
      </p>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">⚡</span>
          <div>
            <p className="font-semibold mb-1">Fast Markets (Major Cities, Airports)</p>
            <p className="mb-0 text-muted-foreground">First booking within <strong>1-7 days</strong> is common if your pricing is competitive. Airport-adjacent listings and popular vehicle types often get inquiries within hours of going live.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-2xl">🚗</span>
          <div>
            <p className="font-semibold mb-1">Average Markets (Suburban, Medium Cities)</p>
            <p className="mb-0 text-muted-foreground">First booking within <strong>1-3 weeks</strong> is typical. You may need to be patient and keep your calendar available.</p>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-2xl">🌲</span>
          <div>
            <p className="font-semibold mb-1">Slow Markets (Rural, Small Towns)</p>
            <p className="mb-0 text-muted-foreground">Could take <strong>3-6 weeks</strong> or longer. Consider whether airport delivery or a different location strategy makes sense.</p>
          </div>
        </div>
      </div>
      
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ Don't Panic If It's Slow at First</p>
        <p className="mb-0">Many successful hosts report their first two weeks being quiet, then suddenly getting multiple booking requests. The algorithm is learning about your listing. Stay patient, keep your calendar open, and don't immediately assume "this doesn't work."</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Your Pricing Strategy: The #1 Factor</h2>
      
      <p>
        If you're worried about getting bookings, <strong>pricing is the lever you control most directly</strong>. And here's a counterintuitive truth: starting low is often the smartest move.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">The "Prove It First" Strategy</h3>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">Week 1-4: Price at or Below Market</p>
        <p className="mb-2">Use Turo's Dynamic Pricing (it's on by default in US, Canada, and UK) and consider setting your floor price slightly below the recommended range. Your goal isn't maximum profit yet—it's getting those first few bookings and reviews.</p>
        <p className="mb-0 text-muted-foreground">Think of it as a customer acquisition cost. You're "buying" reviews and credibility.</p>
      </div>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">Week 5+: Gradually Increase</p>
        <p className="mb-0">Once you have 3-5 positive reviews and understand your local demand, start raising prices. Test different price points. You'll find the sweet spot where you're getting consistent bookings at rates that actually make sense for your business.</p>
      </div>
      
      <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-primary mb-1">💡 Dynamic Pricing Does the Work</p>
        <p className="mb-0">Turo's Dynamic Pricing algorithm automatically adjusts your daily rate based on demand, seasonality, local events, and competition. Opting in (or keeping it on) removes the guesswork and keeps you competitive. You can still set minimum and maximum limits.</p>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Photos Make or Break Your Listing</h2>
      
      <p>
        Turo's official guidance is clear: <strong>listings with 10+ high-quality photos rank higher in search</strong>. This isn't just about looking nice—it directly impacts whether guests click on your listing and whether Turo's algorithm shows you to more people.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Photo Checklist for Maximum Visibility</h3>
      
      <div className="bg-muted/50 rounded-lg p-6 my-6 space-y-3">
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Exterior shots (4-6):</strong> Front, back, both sides, 3/4 angles. Clean background, good lighting (golden hour is ideal).</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Interior shots (3-4):</strong> Dashboard, front seats, back seats, trunk space. Show cleanliness and condition.</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>Feature highlights (2-3):</strong> Touchscreen, sunroof, special features. What makes your car appealing?</div>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-lg">☐</span>
          <div><strong>No clutter:</strong> Remove personal items, trash, air freshener trees. The car should look rental-ready.</div>
        </div>
      </div>
      
      <p>
        <strong>Pro tip:</strong> Take photos when your car is freshly washed and detailed. Shoot during daytime with natural light. Avoid harsh shadows or dark garage shots.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Other Factors That Speed Up Bookings</h2>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">1. Enable Book Instantly</h3>
      
      <p>
        As of 2024, Turo made "Book Instantly" the default for all trips. Guests can book without waiting for host approval. This is <strong>good for new hosts</strong>—it means bookings happen faster, with less friction.
      </p>
      
      <p>
        You still have controls: set minimum trip duration, require certain guest criteria, and you can always message guests after they book. But the instant booking feature removes a barrier that used to slow down new hosts.
      </p>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">2. Offer Delivery (Especially Airport Delivery)</h3>
      
      <p>
        Turo's algorithm explicitly rewards hosts who offer delivery, especially to airports. If you live within reasonable distance of an airport, enabling this feature can significantly boost your visibility in searches.
      </p>
      
      <p>
        You can charge for delivery (many hosts charge $30-$75+ depending on distance), so it's not free labor—it's an additional revenue opportunity that also gets you more bookings.
      </p>

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-amber-600 dark:text-amber-400 mb-1">⚠️ A Word of Caution for Beginners</p>
        <p className="mb-0">Airport delivery can be lucrative, but airports often have <strong>strict rules, permit requirements, and designated pickup zones</strong> that vary widely. Some airports require expensive commercial permits or limit where rideshare/rental pickups can occur. We recommend <strong>starting with home pickups first</strong> to learn the basics of guest handoffs, documentation, and communication. Once you're comfortable with the core hosting process, you can explore airport delivery—we'll cover advanced techniques in a later guide.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">3. Respond Quickly</h3>
      
      <p>
        Your response time affects your ranking. Guests often message multiple hosts—the one who responds first usually gets the booking. Enable notifications on your phone and aim to respond within minutes, not hours.
      </p>
      
      <div className="bg-card border rounded-lg p-4 my-4">
        <p className="font-semibold text-lg mb-2">📱 Response Time Matters</p>
        <p className="mb-0">Turo tracks your average response time. Hosts who consistently respond within 15 minutes rank higher than those who take hours. Fast response also builds guest confidence—they know you're attentive and available.</p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">4. Keep Your Calendar Updated</h3>
      
      <p>
        Nothing kills bookings faster than stale availability. If you block off dates inconsistently or let your calendar go out of sync with your actual availability, guests will book elsewhere. Turo's algorithm also factors in calendar activity when ranking listings.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">What If You're Still Not Getting Bookings?</h2>
      
      <p>
        If it's been 3-4 weeks with zero bookings, here's a troubleshooting checklist:
      </p>
      
      <div className="space-y-4 my-6">
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Check Your Price</p>
          <p className="mb-0">Search for similar cars in your area. Are you priced significantly higher? Try dropping 10-20% for a week and see if inquiries increase.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Upgrade Your Photos</p>
          <p className="mb-0">Compare your photos to top-listed vehicles in your area. Do yours look amateur by comparison? Re-shoot on a sunny day after a detail.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Verify Your Listing Is Live</p>
          <p className="mb-0">Search for your own car in the Turo app (logged out or from a different account). Can you find it? If not, check for any verification issues or listing problems.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Consider Your Location</p>
          <p className="mb-0">If you're in a truly low-demand area, you may need to offer airport delivery or reconsider whether Turo makes sense for your specific situation.</p>
        </div>
        
        <div className="bg-card border rounded-lg p-4">
          <p className="font-semibold text-lg mb-2">Check Your Car Type</p>
          <p className="mb-0">Some vehicle types have more demand than others. Economy cars and SUVs tend to book more consistently than luxury vehicles (smaller market, pickier guests).</p>
        </div>
      </div>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">The Compound Effect of Reviews</h2>
      
      <p>
        Here's the beautiful thing about getting your first few bookings: <strong>it gets easier from there</strong>. Each positive review increases your visibility, builds guest trust, and makes the next booking more likely.
      </p>
      
      <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-4 my-4">
        <p className="font-semibold text-green-600 dark:text-green-400 mb-1">✅ The Momentum Effect</p>
        <p className="mb-0">0 reviews → First booking is hard. 3 reviews → Bookings come easier. 10+ reviews → Bookings become consistent. The first few are the hardest—after that, your reputation does the selling for you.</p>
      </div>
      
      <p>
        Many successful hosts report a "tipping point" around 5-10 reviews where bookings suddenly become much more consistent. Your job is to get through that initial phase.
      </p>

      <hr className="my-8" />

      <h2 className="text-2xl font-bold mt-10">Bottom Line: The Demand Exists</h2>
      
      <div className="bg-primary/5 border-2 border-primary/30 rounded-xl p-6 my-6">
        <p className="text-xl font-semibold text-center mb-4">You Will Get Renters</p>
        <p className="text-center mb-4">
          Turo is a proven marketplace with millions of guests. New hosts get visibility advantages. Your first booking might take a few days or a few weeks—but if you price competitively, take good photos, and stay responsive, <strong>bookings will come</strong>.
        </p>
        <p className="text-center mb-0">
          The hosts who fail aren't the ones who don't get renters—they're the ones who give up after a slow first week or never optimize their listing in the first place. Don't be that person.
        </p>
      </div>

      <h3 className="text-lg font-semibold mt-8 text-muted-foreground">Quick Action Steps</h3>
      
      <ul>
        <li>✅ Use Dynamic Pricing and price at or slightly below market for your first month</li>
        <li>✅ Upload 10+ high-quality photos</li>
        <li>✅ Enable notifications and respond to inquiries within minutes</li>
        <li>✅ Offer airport delivery if feasible</li>
        <li>✅ Keep your calendar accurate and availability open</li>
        <li>✅ Be patient—give it at least 3-4 weeks before worrying</li>
      </ul>
      
      <p>
        Your car is sitting there either way. Might as well list it and start building momentum. 🚗
      </p>
    </div>
  ),
};
